// Typed fetch-обёртки. Все запросы same-origin (raftforge.art),
// cookies отправляются по умолчанию. Mutations идут через `mut()` —
// автоматически подмешивает X-CSRF-Token из module-cache (см. useCsrf).
import { csrf } from './hooks/useCsrf';
import type {
  AdminUser,
  ApiToken,
  DeployPermit,
  MeData,
  ProjectPage,
  ProjectSummary,
  ProxyRecord,
  ProxyStats,
  TelegramLinkToken,
  UserSession,
  WhoAmI,
} from './types';

async function get<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json() as Promise<T>;
}

async function mut<T>(
  url: string,
  method: 'POST' | 'DELETE' = 'POST',
  body?: unknown
): Promise<T | null> {
  const send = async (token: string): Promise<Response> =>
    fetch(url, {
      method,
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': token,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

  let token = await csrf.ensure();
  let r = await send(token);
  if (r.status === 403) {
    csrf.invalidate();
    token = await csrf.ensure();
    r = await send(token);
  }
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  if (r.status === 204) return null;
  return r.json() as Promise<T>;
}

// Стандалон logout: используется из Me (кнопка Выход) и оставлен для
// возможного вызова из других мест. Полный reload на успех — чистый
// state; при ошибке throw наверх.
export async function logout(): Promise<void> {
  const token = await csrf.ensure();
  const send = (t: string) =>
    fetch('/api/me/logout', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-CSRF-Token': t },
    });
  let r = await send(token);
  if (r.status === 403) {
    csrf.invalidate();
    const t2 = await csrf.ensure();
    r = await send(t2);
  }
  if (!r.ok) throw new Error(`logout ${r.status}`);
}

export const api = {
  // Shell config
  whoami: () => get<WhoAmI>('/auth/whoami'),

  // Docs — витрина проектов
  docsProjects: () => get<ProjectSummary[]>('/api/docs/projects'),
  docsProject: (slug: string) =>
    get<ProjectPage>(`/api/docs/p/${encodeURIComponent(slug)}`),

  // Proxy tab — landing проксирует к proxy-service admin API.
  proxyStats: () => get<ProxyStats>('/api/proxy/stats'),
  proxyLogs: (tail = 200) => get<{ records: ProxyRecord[] }>(`/api/proxy/logs?tail=${tail}`),
  proxyStreamUrl: () => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/api/proxy/logs/stream`;
  },

  // /api/me
  me: () => get<MeData>('/api/me'),
  meTokensCreate: (name: string, expires_in_days: number | null) =>
    mut<ApiToken & { token: string }>('/api/me/tokens', 'POST', {
      name,
      expires_in_days,
    }),
  meTokenRevoke: (id: number) =>
    mut<null>(`/api/me/tokens/${id}/revoke`, 'POST'),
  meSessionRevoke: (id: string) =>
    mut<null>(`/api/me/sessions/${encodeURIComponent(id)}/revoke`, 'POST'),
  mePermitCreate: (project: string) =>
    mut<DeployPermit>('/api/me/deploy-permits', 'POST', { project }),
  mePermitRevoke: (id: number) =>
    mut<null>(`/api/me/deploy-permits/${id}/revoke`, 'POST'),
  meSetHandle: (handle: string) =>
    mut<MeData>('/api/me/handle', 'POST', { handle }),
  meTelegramMint: () =>
    mut<TelegramLinkToken>('/api/me/telegram/link-token', 'POST'),
  meTelegramUnlink: () => mut<null>('/api/me/telegram', 'DELETE'),

  // /api/admin
  adminUsers: () => get<AdminUser[]>('/api/admin/users'),
  adminResources: () => get<string[]>('/api/admin/resources'),
  adminUserCreate: (email: string, password: string, curator: boolean) =>
    mut<AdminUser>('/api/admin/users', 'POST', { email, password, curator }),
  adminUserSetPw: (id: number, password: string) =>
    mut<null>(`/api/admin/users/${id}/setpw`, 'POST', { password }),
  adminPromote: (id: number) =>
    mut<AdminUser>(`/api/admin/users/${id}/promote-curator`, 'POST'),
  adminDemote: (id: number) =>
    mut<AdminUser>(`/api/admin/users/${id}/demote-curator`, 'POST'),
  adminGrant: (id: number, resource: string, level: number) =>
    mut<AdminUser>(`/api/admin/users/${id}/grant`, 'POST', { resource, level }),
  adminRevoke: (id: number, resource: string) =>
    mut<AdminUser>(`/api/admin/users/${id}/revoke`, 'POST', { resource }),
  adminSetTelegram: (id: number, telegram_id: string | null, replace = false) =>
    mut<AdminUser>(`/api/admin/users/${id}/telegram`, 'POST', { telegram_id, replace }),
};

// eslint helpers чтобы не жаловался на unused symbol'ы (используются через generic'и)
export type { ApiToken, UserSession, DeployPermit };
